import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { UserProfile } from "../../../shared/types";

interface DashboardLayoutProps {
  user: UserProfile;
  onLogout: () => void;
  children: ReactNode;
  companyName?: string;
  companyTimezone?: string;
}

export function DashboardLayout({
  user,
  onLogout,
  children,
  companyName,
  companyTimezone,
}: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path;
  const isCompanyAdmin = user.userType === "company";
  const isManager = user.userType === "manager";
  const isSuperAdmin = user.userType === "super_admin";

  // Avatar initial: Company admin shows company initial, others show user initial
  const avatarInitial =
    isCompanyAdmin && companyName
      ? companyName[0]?.toUpperCase()
      : (user.firstName?.[0] || user.email?.[0] || "U").toUpperCase();

  const companyDisplayName = companyName || "Company not assigned";
  const userDisplayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email;

  return (
    <div className="dashboard-shell">
      {/* Content Wrapper */}
      <div className="dashboard-wrapper">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          {/* Logo */}
          <div
            className="sidebar-logo"
            style={{ padding: "32px 24px", borderBottom: "1px solid #374151" }}
          >
            <h1 style={{ fontSize: "24px", fontWeight: "700", margin: 0 }}>
              Spechs
            </h1>
            <p
              style={{
                fontSize: "13px",
                color: "#9ca3af",
                margin: "4px 0 0 0",
              }}
            >
              {user.userType === "super_admin"
                ? "Super Admin"
                : user.userType === "company"
                  ? "Company"
                  : "Manager"}{" "}
              Dashboard
            </p>
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav" style={{ flex: 1, padding: "16px 0" }}>
            <NavItem
              label="Dashboard"
              icon={NAV_ICONS.dashboard}
              active={isActive("/")}
              onClick={() => navigate("/")}
            />
            <NavItem
              label="Inbox"
              icon={NAV_ICONS.inbox}
              active={isActive("/messages")}
              onClick={() => navigate("/messages")}
            />
            {user.userType === "super_admin" && (
              <NavItem
                label="Companies"
                icon={NAV_ICONS.companies}
                active={isActive("/companies")}
                onClick={() => navigate("/companies")}
              />
            )}
            {user.userType === "company" && (
              <>
                <NavItem
                  label="Staff"
                  icon={NAV_ICONS.staff}
                  active={isActive("/staff")}
                  onClick={() => navigate("/staff")}
                />
                <NavItem
                  label="Jobs"
                  icon={NAV_ICONS.jobs}
                  active={isActive("/jobs")}
                  onClick={() => navigate("/jobs")}
                />
                <NavItem
                  label="Time Entries"
                  icon={NAV_ICONS.approvals}
                  active={isActive("/time-entries")}
                  onClick={() => navigate("/time-entries")}
                />
                <NavItem
                  label="Approvals"
                  icon={NAV_ICONS.approvals}
                  active={isActive("/approvals")}
                  onClick={() => navigate("/approvals")}
                />
                <NavItem
                  label="Reports"
                  icon={NAV_ICONS.reports}
                  active={isActive("/reports")}
                  onClick={() => navigate("/reports")}
                />
                <NavItem
                  label="Settings"
                  icon={NAV_ICONS.settings}
                  active={isActive("/settings")}
                  onClick={() => navigate("/settings")}
                />
              </>
            )}
            {user.userType === "manager" && (
              <>
                <NavItem
                  label="Jobs"
                  icon={NAV_ICONS.jobs}
                  active={isActive("/jobs")}
                  onClick={() => navigate("/jobs")}
                />
                <NavItem
                  label="Team"
                  icon={NAV_ICONS.staff}
                  active={isActive("/team")}
                  onClick={() => navigate("/team")}
                />
                <NavItem
                  label="Time Entries"
                  icon={NAV_ICONS.approvals}
                  active={isActive("/time-entries")}
                  onClick={() => navigate("/time-entries")}
                />
                <NavItem
                  label="Approvals"
                  icon={NAV_ICONS.reports}
                  active={isActive("/approvals")}
                  onClick={() => navigate("/approvals")}
                />
              </>
            )}
          </nav>

          {/* User Section */}
          <div
            className="sidebar-user"
            style={{
              padding: "16px 16px 32px 16px",
              borderTop: "1px solid #374151",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  fontWeight: "600",
                  marginRight: "12px",
                  flexShrink: 0,
                }}
              >
                {avatarInitial}
              </div>
              <div style={{ flex: 1, overflow: "hidden" }}>
                {/* Company Admin: Emphasize company name */}
                {isCompanyAdmin && (
                  <>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#ffffff",
                        marginBottom: "2px",
                      }}
                    >
                      {companyDisplayName}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginBottom: "1px",
                      }}
                    >
                      {userDisplayName}
                    </div>
                    <div style={{ fontSize: "10px", color: "#6b7280" }}>
                      Company Administrator
                    </div>
                  </>
                )}
                {/* Manager: Emphasize manager's name */}
                {isManager && (
                  <>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#ffffff",
                        marginBottom: "2px",
                      }}
                    >
                      {userDisplayName}
                    </div>
                    <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                      Manager at {companyDisplayName}
                    </div>
                  </>
                )}
                {/* Super Admin: Show user name */}
                {isSuperAdmin && (
                  <>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#ffffff",
                      }}
                    >
                      {userDisplayName}
                    </div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                      Super Admin
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onLogout}
              style={{
                width: "100%",
                padding: "10px 16px",
                backgroundColor: "#374151",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#374151";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {/* Top Bar */}
          <div className="dashboard-topbar">
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {new Date().toLocaleDateString("en-IE", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                ...(companyTimezone && { timeZone: companyTimezone }),
              })}
            </div>
          </div>

          {/* Page Content */}
          <main className="dashboard-content">{children}</main>
        </div>
      </div>
    </div>
  );
}

interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

function NavItem({ label, icon, active, onClick }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      data-active={active}
      style={{
        width: "100%",
        padding: "12px 24px",
        backgroundColor: active ? "#374151" : "transparent",
        color: active ? "#ffffff" : "#9ca3af",
        border: "none",
        borderLeft: "none",
        textAlign: "left",
        fontSize: "15px",
        fontWeight: active ? "600" : "400",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        transition: "all 0.2s",
        outline: "none",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "#374151";
          e.currentTarget.style.color = "#ffffff";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "#9ca3af";
        }
      }}
    >
      <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {icon}
      </span>
      {label}
    </button>
  );
}

const NAV_ICONS = {
  dashboard: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  inbox: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  companies: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  staff: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  jobs: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="13" y2="16" />
    </svg>
  ),
  approvals: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  reports: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  settings: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};
